import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  companyQuery,
  engagementProfileQuery,
  ledgerQuery,
  membersQuery,
  pendingSetsQuery,
  productsQuery,
  stakeholdersQuery,
  workItemsQuery,
} from '../api/jedi'
import { useEngagement } from '../lib/engagement'
import { usePersonZone } from '../lib/personTime'
import { AddPersonDialog } from '../shell/AddPersonDialog'
import { LoadProblem } from '../shell/LoadProblem'
import { PersonPanel } from '../shell/PersonPanel'
import { productName } from '../shell/PlatformFields'
import { TeamDirectory } from '../shell/TeamDirectory'
import { type BoardRow, TeamLoadBoard } from '../shell/TeamLoadBoard'
import { recordSearch, useLinkedRecord } from '../shell/recordLink'
import { type PartyKey, loadFor, objectIdsByStakeholder, partyOf } from '../shell/teamLoad'

export const Route = createFileRoute('/_shell/team')({
  component: TeamView,
  // A cited record's door (wave 23): the chat's record chip lands here.
  validateSearch: recordSearch,
})

/**
 * Team — who is on this engagement, what they carry, and how to reach them.
 *
 * TWO VIEWS OF ONE LIST (ADR-0031 §8 — Stakeholders are who EXISTS; Party is
 * who-owns on a record; Audience is who-sees; never collapsed). Directory is
 * everybody, grouped by which side of the table they are on. My Team is the
 * internal-party load lens, which C-037 permits by name.
 *
 * TWO DOORS, AND BOTH DRAFT. Adding somebody opens a dialog; correcting a
 * detail is a sentence composed in their own drawer. Neither writes a record:
 * both go to the drafting door for an attributed approval, which is C-033's
 * first clause and the reason `useReassign` is legal. Whether a form like this
 * is the FIELD EDITOR C-033's second clause forbids is the open question
 * ADR-0052 frames; the mechanism underneath is the one the constraint requires
 * either way.
 */
function TeamView() {
  const { id: engagementId } = useEngagement()
  const zone = usePersonZone()
  const people = useQuery(stakeholdersQuery(engagementId))
  // Who a directory entry signs in as, through the binding act (ADR-0044 §4).
  // Without it no row can be joined to any work, because the two populations
  // share no field and matching on a name would be a guess.
  const members = useQuery(membersQuery(engagementId))
  // The account-scope lens, which is the same read Work's All lens makes, so
  // the counts here and the rows there cannot drift apart.
  const items = useQuery(workItemsQuery(engagementId, 'all'))
  // What each person is accountable for, which is the one thing the drawer can
  // say about a contact that their own row cannot.
  const ledger = useQuery(ledgerQuery(engagementId))
  const profile = useQuery(engagementProfileQuery(engagementId))
  const products = useQuery(productsQuery)
  // Anything J.E.D.I. has proposed about a person, so the surface says so where
  // the people are rather than only on Home.
  const pending = useQuery(pendingSetsQuery(engagementId))
  /*
    WHO WE ARE, which is not the engagement's `organizationName`. That field is
    the CLIENT's parent (ADR-0025: Johns Hopkins, or a prime such as ServiceNow);
    this is the company delivering the work. The add form read the former for
    want of the latter, which would have filed an Intact colleague under the
    client's organisation.
  */
  const company = useQuery(companyQuery)

  const [view, setView] = useState<'directory' | 'mine'>('directory')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<PartyKey | 'everyone'>('everyone')
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null)
  const [adding, setAdding] = useState(false)
  /** An address the add form should open already knowing. */
  const [prefill, setPrefill] = useState<string | null>(null)
  const [said, setSaid] = useState<string | null>(null)

  const { record } = Route.useSearch()
  useLinkedRecord(record, people.data, (person) => person.id,
    (person) => setSelected({ id: person.id, name: person.name }))
  const everyone = people.data ?? []
  const selectedPerson = everyone.find((p) => p.id === selected?.id) ?? null

  const select = (id: string, name: string) =>
    setSelected((current) => (current?.id === id ? null : { id, name }))

  const objectIds = objectIdsByStakeholder(members.data ?? [])
  const boardRows: BoardRow[] = everyone
    .filter((p) => partyOf(p) === 'intact')
    .map((person) => ({
      person,
      load: objectIds.has(person.id)
        ? loadFor(objectIds.get(person.id)!, items.data ?? [], zone)
        : null,
    }))

  /*
    What J.E.D.I. has proposed about the directory, counted from the same
    pending list Home decides on. The banner is a POINTER, never a second place
    to decide: proposals are decided on Home and nowhere else (B-1, the routing
    law), so this says how many and takes you there.
  */
  const proposedPeople = (pending.data ?? []).filter((set) =>
    (set.parts ?? []).some((part) => (part.nodeType ?? '').toLowerCase() === 'stakeholder'))

  return (
    <section className="surface surface-wide">
      <header className="surface-head">
        <div className="team-title">
          <h1>Team</h1>
          {everyone.length > 0 && <span className="team-count">{everyone.length}</span>}
        </div>
        <p className="surface-sub">
          {/* The product's name, not its stored identifier ("servicenow"). */}
          {[profile.data?.customerName, productName(products.data, profile.data?.product)].filter(Boolean).join(' · ')
            || 'Everyone on this engagement: Intact, customer, third-party.'}
        </p>
        <div className="team-views" role="group" aria-label="How to look at the team">
          <button
            type="button"
            className={view === 'directory' ? 'team-view team-view-on' : 'team-view'}
            aria-pressed={view === 'directory'}
            onClick={() => setView('directory')}
          >
            Directory
          </button>
          <button
            type="button"
            className={view === 'mine' ? 'team-view team-view-on' : 'team-view'}
            aria-pressed={view === 'mine'}
            onClick={() => setView('mine')}
          >
            My Team
          </button>
          {/*
            ONE BUTTON, NOT TWO. Which side somebody is on is the dialog's
            first question rather than a choice made before it opens: a person
            adding a name should not have to classify them before they have
            typed one.
          */}
          <button
            type="button"
            className="team-add"
            onClick={() => { setSaid(null); setPrefill(null); setAdding(true) }}
          >
            + Add person
          </button>
        </div>
      </header>

      {said && <p className="surface-note">{said}</p>}
      {people.isPending && <p className="surface-note">Loading…</p>}
      <LoadProblem error={people.error ?? members.error ?? items.error} />

      {proposedPeople.length > 0 && (
        <p className="team-proposed">
          <span className="team-proposed-tag">Proposed by J.E.D.I.</span>
          {proposedPeople.length === 1
            ? ' One proposal about who is on this engagement is waiting for you on Home.'
            : ` ${proposedPeople.length} proposals about who is on this engagement are waiting for you on Home.`}
        </p>
      )}

      <div className="work-layout">
        <div className="work-main">
          {view === 'directory' ? (
            <TeamDirectory
              engagementId={engagementId}
              people={everyone}
              members={members.data ?? []}
              items={items.data ?? []}
              ledger={ledger.data ?? []}
              zone={zone}
              customerName={profile.data?.customerName ?? null}
              companyName={company.data?.name ?? null}
              search={search}
              onSearch={setSearch}
              filter={filter}
              onFilter={setFilter}
              selectedId={selected?.id ?? null}
              onSelect={(person) => select(person.id, person.name)}
              // Somebody who can already sign in and has no entry. The form
              // opens knowing their address, so it binds rather than grants.
              onAddMember={(upn) => { setSaid(null); setPrefill(upn); setAdding(true) }}
            />
          ) : (
            <TeamLoadBoard
              rows={boardRows}
              selectedId={selected?.id ?? null}
              onSelect={(person) => select(person.id, person.name)}
            />
          )}
        </div>

        {/*
          A PERSON'S OWN PANEL, not the record drawer (Erik, 2026-09-25:
          *"When I click a contact it's opening in chat. That is not the
          desired outcome for these records."*). The drawer is for working a
          record and carries a conversation beside it; a directory entry is
          something you read before a call and has nothing to decide.

          NULL WHERE THEY HAVE NO LOGIN, and it is not the same as a zero.
          `loadFor` answers an unbound person with an empty load, which is the
          right answer to "what are they carrying" and the wrong thing to hand
          a renderer: it made Ellen Cho read "carrying nothing open right now",
          which says she is a user with an empty plate rather than somebody who
          is not a user at all.
        */}
        {selectedPerson && (
          <PersonPanel
            engagementId={engagementId}
            person={selectedPerson}
            load={objectIds.has(selectedPerson.id)
              ? loadFor(objectIds.get(selectedPerson.id)!, items.data ?? [], zone)
              : null}
            ledger={ledger.data ?? []}
            upn={(members.data ?? []).find(
              (m) => m.stakeholderId === selectedPerson.id)?.upn ?? null}
            onClose={() => setSelected(null)}
          />
        )}
      </div>

      <AddPersonDialog
        open={adding}
        engagementId={engagementId}
        people={everyone}
        customerName={profile.data?.customerName ?? null}
        companyName={company.data?.name ?? null}
        prefillAddress={prefill}
        onClose={() => { setAdding(false); setPrefill(null) }}
        onAdded={setSaid}
      />
    </section>
  )
}
